import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  query,
  where,
  updateDoc,
  getDocs
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { Usuario } from '../models/usuario.model';
import { Residente } from '../models/residente.model';

interface CrearResidentePayload {
  residencialId: string;
  nombre: string;
  correo: string;
  unidad: string;
  idNacional?: string;
  telefono?: string;
  permitirLogin: boolean;
}

interface CrearResidenteResult {
  residenteId: string;
}

interface ActualizarResidentePayload {
  residenteId: string;
  nombre: string;
  correo: string;
  unidad: string;
  idNacional?: string;
  telefono?: string;
  permitirLogin: boolean;
}

interface ActualizarResidenteResult {
  residenteId: string;
}

interface EliminarResidentePayload {
  residenteId: string;
}

interface EliminarResidenteResult {
  residenteId: string;
}

interface ImportarResidentesPayload {
  residencialId: string;
  filas: {
    nombre: string;
    correo: string;
    unidad: string;
    idNacional?: string;
    telefono?: string;
    permitirLogin: boolean;
  }[];
}

interface ImportarResidentesResult {
  resultados: {
    nombre:     string;
    correo:     string;
    exito:      boolean;
    errorCode?: 'email-existe' | 'limite-alcanzado' | 'interno';
  }[];
}

@Injectable({ providedIn: 'root' })
export class ResidenteService {
  private firestore = inject(Firestore);
  private storage   = inject(Storage);
  private functions = inject(Functions);

  // ── Lectura — Admin ───────────────────────────────────────────────────────

  // listener en tiempo real — usado por ResidencialStateService para el signal compartido
  getAll(residencialId: string): Observable<Residente[]> {
    const col = collection(this.firestore, `residenciales/${residencialId}/residentes`);
    const q   = query(col, where('activo', '==', true));
    return collectionData(q, { idField: 'id' }) as Observable<Residente[]>;
  }

  // lectura puntual — usado en el CRUD como fallback si el signal aún no cargó el residente
  getById(residencialId: string, id: string): Observable<Residente> {
    const r = doc(this.firestore, `residenciales/${residencialId}/residentes/${id}`);
    return docData(r, { idField: 'id' }) as Observable<Residente>;
  }

  // perfil global del admin — usado para verificar residencialId en operaciones del panel
  getAdminProfile(uid: string): Observable<Usuario> {
    const r = doc(this.firestore, `usuarios/${uid}`);
    return docData(r, { idField: 'id' }) as Observable<Usuario>;
  }

  // ── Cloud Functions — Admin (escritura vía CF) Auth y Firestore desde la función ─────────────────────────────

  // crea Auth user + custom claims + docs en usuarios y residentes — usado en CRUD e import
  async crearResidente(payload: CrearResidentePayload): Promise<CrearResidenteResult> {
    const fn = httpsCallable<CrearResidentePayload, CrearResidenteResult>(
      this.functions, 'crearResidente'
    );
    const result = await fn(payload);
    return result.data;
  }

  // actualiza Auth + usuarios + residentes — usado en CRUD (modo editar)
  async actualizarResidente(payload: ActualizarResidentePayload): Promise<ActualizarResidenteResult> {
    const fn = httpsCallable<ActualizarResidentePayload, ActualizarResidenteResult>(
      this.functions, 'actualizarResidente'
    );
    const result = await fn(payload);
    return result.data;
  }

  // soft delete: activo = false en usuarios y residentes — usado en CRUD (btn eliminar)
  async eliminarResidente(residenteId: string): Promise<void> {
    const fn = httpsCallable<EliminarResidentePayload, EliminarResidenteResult>(
      this.functions, 'eliminarResidente'
    );
    await fn({ residenteId });
  }

  // crea N residentes en un solo llamado CF — usado en import masivo
  async importarResidentes(payload: ImportarResidentesPayload): Promise<ImportarResidentesResult> {
    const fn = httpsCallable<ImportarResidentesPayload, ImportarResidentesResult>(
      this.functions, 'importarResidentes'
    );
    const result = await fn(payload);
    return result.data;
  }

  // ── Firestore directo — Admin (escritura) ─────────────────────────────────

  // actualización parcial directa — usado en CRUD para guardar fotosUrl tras subir imágenes
  async update(residencialId: string, id: string, data: Partial<Omit<Residente, 'id' | 'creadoEn'>>): Promise<void> {
    const r = doc(this.firestore, `residenciales/${residencialId}/residentes/${id}`);
    await updateDoc(r, { ...data });
  }

  // ── Storage — Admin ───────────────────────────────────────────────────────

  // sube imágenes a Storage y devuelve sus URLs — usado en CRUD al guardar con fotos nuevas
  async uploadFotos(
    residencialId: string,
    residenteId: string,
    files: File[]
  ): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const ext     = file.name.split('.').pop() ?? 'png';
      const ts      = Date.now();
      const fotoRef = ref(
        this.storage,
        `residenciales/${residencialId}/residentes/${residenteId}/fotos/${ts}.${ext}`
      );
      await uploadBytes(fotoRef, file);
      urls.push(await getDownloadURL(fotoRef));
    }
    return urls;
  }

  // elimina una foto de Storage por URL — usado en CRUD al quitar fotos en modo editar
  async deleteFoto(url: string): Promise<void> {
    try {
      await deleteObject(ref(this.storage, url));
    } catch { /* ignorar si ya fue eliminada */ }
  }

  // ── Helpers — Admin (validación) ──────────────────────────────────────────

  // valida un solo correo contra usuarios — usado en CRUD antes de crear o al cambiar email
  async emailExiste(email: string, excludeId: string): Promise<boolean> {
    const normalized = email.toLowerCase().trim();
    const col  = collection(this.firestore, 'usuarios');
    const q    = query(col, where('email', '==', normalized));
    const snap = await getDocs(q);
    return snap.docs.some(d => d.id !== excludeId);
  }

  // valida N correos en lote (chunks de 30 en paralelo) — usado en import para evitar N queries secuenciales
  async emailsExisten(emails: string[], excludeId = ''): Promise<Set<string>> {
    const normalized = [...new Set(emails.map(e => e.toLowerCase().trim()))].filter(Boolean);
    if (!normalized.length) return new Set();

    const chunks: string[][] = [];
    for (let i = 0; i < normalized.length; i += 30) {
      chunks.push(normalized.slice(i, i + 30));
    }

    const existentes = new Set<string>();
    await Promise.all(chunks.map(async chunk => {
      const q    = query(collection(this.firestore, 'usuarios'), where('email', 'in', chunk));
      const snap = await getDocs(q);
      snap.docs
        .filter(d => d.id !== excludeId)
        .forEach(d => existentes.add((d.data()['email'] as string).toLowerCase()));
    }));

    return existentes;
  }

}
